interface DotRatingProps {
  value: number; // 0-5; 0 = unrated
  max?: number;
  color?: 'green' | 'purple';
  editable?: boolean;
  onChange?: (value: number) => void;
  size?: 'sm' | 'md';
}

const COLOR_CLASSES: Record<'green' | 'purple', { filled: string; ring: string }> = {
  green: { filled: 'bg-green-500', ring: 'hover:ring-green-300' },
  purple: { filled: 'bg-purple-500', ring: 'hover:ring-purple-300' },
};

/**
 * 1-5 dot rating control. Filled dots up to `value`; empty dots are gray.
 * When editable, clicking a dot sets the value to that dot's index; clicking the
 * currently-highest filled dot again clears the rating to 0.
 */
export function DotRating({
  value,
  max = 5,
  color = 'green',
  editable = false,
  onChange,
  size = 'md',
}: DotRatingProps) {
  const colors = COLOR_CLASSES[color];
  const dotSize = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';

  const handleClick = (index: number) => {
    if (!editable || !onChange) return;
    // Clicking the highest filled dot again clears the rating
    onChange(value === index ? 0 : index);
  };

  return (
    <div className="inline-flex items-center gap-1">
      {Array.from({ length: max }, (_, i) => {
        const dotValue = i + 1;
        const isFilled = dotValue <= value;
        return (
          <button
            key={dotValue}
            type="button"
            disabled={!editable}
            onClick={() => handleClick(dotValue)}
            title={editable ? `Set to ${dotValue}` : `${value} of ${max}`}
            aria-label={`${dotValue} of ${max}`}
            className={`${dotSize} rounded-full transition-all ${
              isFilled ? colors.filled : 'bg-gray-200'
            } ${
              editable
                ? `cursor-pointer hover:ring-2 ${colors.ring}`
                : 'cursor-default'
            }`}
          />
        );
      })}
    </div>
  );
}
